import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  useParams,
  useOutletContext,
  useNavigate,
  useLocation
} from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Alert,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { remainingDays, formatJpDate } from "../../../../utils/dateUtils";
import InquiryButton from "../../../shared/InquiryButton";
import { handleApply } from "../../../utils/handleApply";
import { LevelTooltipText, levelMap } from "../../../../shared/Constants";
import ContentLockOverlay from "../../../shared/ContentLockOverlay";
import { useUnread } from "../../../../shared/unread/UnreadProvider";

const ProjectDetailPage = ({ csrfToken }) => {
  const navigate = useNavigate();

  // ルートパラメータからIDを取得
  const { id } = useParams();

  // プロジェクト情報のステート管理
  const [project, setProject] = useState(null);
  const [contractExists, setContractExists] = useState(true);

  // 公開状態（false = 非公開、true = 公開中）
  const [isActive, setIsActive] = useState(false);

  // 概要行の DOM の位置
  const rowRefs = useRef([]);
  const [overlayTop, setOverlayTop] = useState(null);

  // テーマ情報とレスポンシブ判定
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [switchChecked, setSwitchChecked] = useState(false);

  // メッセージ受けとる
  const location = useLocation();
  const flashMessage = location.state?.flashMessage;

  // 既読
  const { handleMarkedRead } = useOutletContext();
  const { markProjectRead } = useUnread();

  // textareaの改行設定
  const preLineLabels = [
    "備考",
    "概要",
    "スキル詳細",
    "工程詳細",
    "ポジション詳細",
    "商流詳細",
  ];

  // work_style の enum値と日本語ラベルを同期
  const WORK_STYLES = {
    on_site: "常駐",
    remote: "フルリモート",
    hybrid: "ハイブリッド",
  };

  // commercial_flow の enum値と日本語ラベルを同期
  const COMMERCIAL_FLOW = {
    end_client_company: "エンド直",
    primary_contractor_company: "元受け直",
    other: "その他",
  };

  // 初期表示でプロジェクトデータを取得（idが存在する場合）
  useEffect(() => {
    const abortController = new AbortController();

    fetch(`/projects/${id}.json`, {
      signal: abortController.signal,
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (abortController.signal.aborted) return;

      setProject(data);
      setContractExists(data.contract_request_exists);
      setSwitchChecked(data.published_flag);
      setIsActive(data.recruiting_flag);

      const numericProjectId = Number(id);
      if (!Number.isNaN(numericProjectId)) {
        markProjectRead(numericProjectId);
        handleMarkedRead(numericProjectId);
      }
    })
    .catch((error) => {
      if (error.name === "AbortError") return;
      console.error("プロジェクトの取得中にエラーが発生しました", error);
    });

    return () => {
      abortController.abort();
    };
  }, [id, handleMarkedRead]);

  // ProjectLayout のトグル変更をリアルタイムで受け取る
  useEffect(() => {
    const handler = (e) => {
      setIsActive(e.detail.recruiting_flag);
    };

    window.addEventListener("contract:status-updated", handler);

    return () => {
      window.removeEventListener("contract:status-updated", handler);
    };
  }, []);

  const rows = useMemo(() => {
    if (!project) return [];

    return [
      { label: "プロジェクト名", value: project.name || "未設定" },
      {
        label: "金額",
        value:
          project.price_min && project.price_max
            ? `${project.price_min}〜${project.price_max}万円`
            : "未設定",
      },
      {
        label: "稼働形態",
        value: project.work_style
          ? WORK_STYLES[project.work_style] || "未設定"
          : "未設定",
      },
      // 稼働形態がリモート以外（=ハイブリッド or 常駐）なら作業場所を表示
      ...(project.work_style === "hybrid" || project.work_style === "on_site"
        ? [
            {
              label: "作業場所",
              value:
                Array.isArray(project.prefecture_names) &&
                project.prefecture_names.length > 0
                  ? project.prefecture_names.join("、")
                  : "未設定",
            },
          ]
        : []),
      {
        label: "スキル",
        value:
          Array.isArray(project.skills) && project.skills.length > 0
            ? project.skills.map((s) => {
                const year =
                  s.experience_year != null
                    ? `${s.experience_year}年以上`
                    : "年数未設定";
                const level =
                  s.level != null
                    ? `レベル${levelMap[s.level] || s.level}以上`
                    : "レベル未設定";
                return `${s.skill_name}（${year}、${level}）`;
              })
            : ["未設定"],
      },
      { label: "スキル詳細", value: project.skill_detail || "なし" },
      { label: "概要", value: project.description || "未設定" },
      {
        label: "稼働期間",
        value: project.work_start_date
          ? project.long_term_flag
            ? `${formatJpDate(project.work_start_date)}〜長期`
            : project.work_end_date
              ? `${formatJpDate(project.work_start_date)}〜${formatJpDate(
                  project.work_end_date,
                )}`
              : `${formatJpDate(project.work_start_date)}〜未設定`
          : "未設定",
      },
      {
        label: "想定稼働",
        value:
          project.workload_min && project.workload_max
            ? `${project.workload_min}〜${project.workload_max}h`
            : "未設定",
      },

      {
        label: "商流",
        value: COMMERCIAL_FLOW[project.commercial_flow] || "未設定",
      },
      {
        label: "商流詳細",
        value: project.commercial_flow_detail || "なし",
      },
      {
        label: "工程",
        value:
          Array.isArray(project.operations) && project.operations.length > 0
            ? project.operations
                .sort((a, b) => a.operation_id - b.operation_id)
                .map((p) => {
                  const year =
                    p.experience_year != null
                      ? `${p.experience_year}年以上`
                      : "年数未設定";
                  return `${p.operation_name}（${year}）`;
                })
            : ["未設定"],
      },
      { label: "工程詳細", value: project.operation_detail || "なし" },
      {
        label: "ポジション",
        value:
          Array.isArray(project.roles) && project.roles.length > 0
            ? project.roles.map((p) => {
                const year =
                  p.experience_year != null
                    ? `${p.experience_year}年以上`
                    : "年数未設定";
                return `${p.role_name}（${year}）`;
              })
            : ["未設定"],
      },
      { label: "ポジション詳細", value: project.role_detail || "なし" },
      {
        label: "面談回数",
        value: project.interview_count
          ? `${project.interview_count}`
          : "未設定",
      },
      { label: "備考", value: project.remark || "なし" },
    ];
  }, [project]);

  // スキル詳細の index
  const mosaicStartIndex = rows.findIndex((r) => r.label === "スキル詳細");

  const mosaicStyle = {
    filter: "blur(6px)",
    pointerEvents: "none",
    userSelect: "none",
  };

  // DOM から「スキル詳細」の行の位置を取得
  useEffect(() => {
    if (!project) return;
    const target = rowRefs.current[0];
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const paper = target.closest(".blur-paper");
    const paperRect = paper.getBoundingClientRect();

    setOverlayTop(rect.top - paperRect.top);
  }, [project]);

  if (project === null) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* フラッシュメッセージ*/}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        {/* 左：戻るリンク */}
        <Button
          onClick={() => navigate("/projects")}
          size="small"
          sx={{
            mb: 0.5,
            color: "#000",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { opacity: 0.7 },
          }}
        >
          ← 戻る
        </Button>

        {/* 詳細本体エリア */}
        <Paper
          elevation={3}
          className="blur-paper"
          sx={{
            borderRadius: 2,
            p: { xs: 2, sm: 4 },
            position: "relative",
            backgroundColor: "F9FAFF",
          }}
        >
          {/* スキル詳細より下だけオーバーレイ */}
          {!contractExists && overlayTop !== null && (
            <ContentLockOverlay top={overlayTop} type="project" />
          )}

          {/* 掲載終了日と残日数 */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              alignItems: { xs: "flex-start", sm: "center" },
              textAlign: { xs: "left", sm: "inherit" },
            }}
          >
            <Typography fontSize="12px" color="gray">
              掲載期間 {formatJpDate(project.publish_end_date)}まで
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              {remainingDays(project.publish_end_date)}
            </Typography>
          </Box>

          {/* タイトル */}
          <Typography
            variant="h5"
            fontWeight="bold"
            mb={2}
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem" },
            }}
          >
            {project.title}
          </Typography>

          {/* 情報テーブル */}
          <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
            <TableBody>
              {rows.map((row, index) =>
                isMobile ? (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#4b5563" : "#9ca3af",
                          color: "white",
                          py: 1,
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.label}
                        {row.label === "スキル" && (
                          <Box>
                            <LevelTooltipText
                              fontSize={10}
                              color="rgba(255, 255, 255, 0.8)"
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        ref={
                          index === mosaicStartIndex
                            ? (el) => (rowRefs.current[0] = el)
                            : null
                        }
                        style={
                          !contractExists && index >= mosaicStartIndex
                            ? mosaicStyle
                            : {}
                        }
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                          fontSize: "0.8rem",
                          whiteSpace: preLineLabels.includes(row.label)
                            ? "pre-line"
                            : undefined,
                          wordBreak: preLineLabels.includes(row.label)
                            ? "break-all"
                            : undefined,
                        }}
                      >
                        {/* 希望条件が未入力の場合、スキル詳細以降は伏せ字メッセージに差し替える */}
                        {(() => {
                          const displayedValue =
                            !contractExists && index >= mosaicStartIndex
                              ? "希望条件を入力すると詳細が確認できます"
                              : row.value;

                          return Array.isArray(displayedValue)
                            ? displayedValue.map((v, i) => (
                                <div key={i}>{v}</div>
                              ))
                            : displayedValue;
                        })()}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ) : (
                  <TableRow key={index}>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#4b5563" : "#9ca3af",
                        color: "white",
                        width: "25%",
                      }}
                    >
                      {row.label}
                      {row.label === "スキル" && (
                        <Tooltip
                          title={<LevelTooltipText />}
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              ml: 0.5,
                              verticalAlign: "middle",
                              color: "white",
                              opacity: 0.7,
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      ref={
                        index === mosaicStartIndex
                          ? (el) => (rowRefs.current[0] = el)
                          : null
                      }
                      style={
                        !contractExists && index >= mosaicStartIndex
                          ? mosaicStyle
                          : {}
                      }
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                        whiteSpace: preLineLabels.includes(row.label)
                          ? "pre-line"
                          : undefined,
                        wordBreak: preLineLabels.includes(row.label)
                          ? "break-all"
                          : undefined,
                      }}
                    >
                      {/* 希望条件が未入力の場合、スキル詳細以降は伏せ字メッセージに差し替える */}
                      {(() => {
                        const displayedValue =
                          !contractExists && index >= mosaicStartIndex
                            ? "希望条件を入力すると詳細が確認できます"
                            : row.value;

                        return Array.isArray(displayedValue)
                          ? displayedValue.map((v, i) => <div key={i}>{v}</div>)
                          : displayedValue;
                      })()}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* 希望条件が入力済みかつエージェントに公開中のときのみ表示 */}
      {contractExists && isActive && (
        <Box
          sx={{
            position: "fixed",
            zIndex: 60,
            right: { xs: 0, sm: 24 },
            bottom: {
              xs: "env(safe-area-inset-bottom)",
              sm: 24,
            },
            width: { xs: "100%", sm: "auto" },
            p: { xs: 1.5, sm: 0 },
            backgroundColor: {
              xs: "rgba(0, 0, 0, 0.5)",
              sm: "transparent",
            },
            boxShadow: {
              xs: "0 -4px 12px rgba(0,0,0,0.12)",
              sm: "none",
            },
          }}
        >
          <InquiryButton
            isMobile={isMobile}
            onClick={() =>
              handleApply({
                type: "projects",
                id: project.id,
                csrfToken,
              })
            }
            label="詳細を聞きたい"
            tooltip="運営局に問い合わせメールが送信されます"
            type="project"
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default ProjectDetailPage;
